import axios from 'src/utils/axios';

/**
 * setupAssistantApi — CON-1699.
 *
 * salespersonId comes off the session on every call; nothing here names
 * one. The three writes take no body and return acknowledgements only.
 * The CON-1700 ask sends the step's systemCode and the question.
 */

const BASE = '/api/setup-assistant';

export const fetchSetupAssistant = async () => {
  const { data } = await axios.get(BASE);
  return data;
};

// The /app destination probe. Resolves to { shouldShow, reason, daysShown };
// the caller acts on shouldShow only. NOT a page load: it does not build the
// page. It MAY spend one of the account's seven default-screen days, which
// is why only the /app destination check calls it (see
// components/SetupAssistantDefaultScreen.jsx) and nothing else should.
export const fetchSetupAssistantDefaultScreen = async () => {
  const { data } = await axios.get(`${BASE}/default-screen`);
  return data;
};

// Read-only "is verified setup complete". Resolves to { verifiedComplete,
// outstandingCount, outstandingSystemCodes, showBadges }; all four are null
// when the server could not read the account (unknown, not incomplete).
// showBadges is true only once the account is past its default-screen phase
// and something is outstanding.
// Writes nothing on the server: no first-seen, no default-screen day. This
// is the call for a surface that is NOT the Setup Assistant page and must
// not spend anything - the TCR submitted modal uses it to decide which copy
// to show, and the sidebar badge (actions/setupAssistantActions) for its
// count. Do not use fetchSetupAssistant or fetchSetupAssistantDefaultScreen
// for that question.
export const fetchSetupAssistantVerifiedComplete = async () => {
  const { data } = await axios.get(`${BASE}/verified-complete`);
  return data;
};

export const postRuleOpened = async (ruleId) => {
  const { data } = await axios.post(`${BASE}/rules/${ruleId}/opened`);
  return data;
};

export const postRuleChecked = async (ruleId) => {
  const { data } = await axios.post(`${BASE}/rules/${ruleId}/checked`);
  return data;
};

export const deleteRuleChecked = async (ruleId) => {
  const { data } = await axios.delete(`${BASE}/rules/${ruleId}/checked`);
  return data;
};

// The opt-out checkbox, checked and unchecked. No body: the server stores a
// fixed value. Both answer 404 when the step does not offer the choice.
export const postRuleOptOut = async (ruleId) => {
  const { data } = await axios.post(`${BASE}/rules/${ruleId}/opt-out`);
  return data;
};

export const deleteRuleOptOut = async (ruleId) => {
  const { data } = await axios.delete(`${BASE}/rules/${ruleId}/opt-out`);
  return data;
};

// CON-1700. One question, one answer, no thread: `history` is part of the
// endpoint's contract and is deliberately not sent in v1. Resolves to
// { available: true, answer } or { available: false, message }.
export const postAskAboutStep = async (systemCode, question) => {
  const { data } = await axios.post(`${BASE}/ask`, { systemCode, question });
  return data;
};

// A write answers 404 when the rule is not applicable to this account. That
// is "the page is stale, refetch", not an error to toast.
export const isStalePageError = (error) => error?.response?.status === 404;

// Referral onboarding block. Resolves to { state: 'none' | 'offered' |
// 'applied', ... }: offered carries { leaderName, preview } (the regular
// recipient preview), applied carries the display-only record. Always 200;
// the server answers 'none' rather than an error when there is no block.
export const fetchReferralSet = async () => {
  const { data } = await axios.get(`${BASE}/referral-set`);
  return data;
};

// Apply the offered referral set. Sends only the selected Set item ids and
// the subscribe choice; the server finds the Set from the user's own leader.
// 200 { success, results, totals, referralSet }. A 409 (already applied,
// no longer offered) or a 400 that carries referralSet (copying started and
// then failed) means the server state moved: the caller re-fetches. A 400
// without referralSet, a 503 (maintenance) or a network error means nothing
// was copied and the block stays offered.
// eventChoices: the "Link your events" answers (one per visible event card).
export const postReferralSetApply = async (selectedSetItemIds, subscribeToUpdates, eventChoices) => {
  const { data } = await axios.post(`${BASE}/referral-set/apply`, {
    selectedSetItemIds,
    subscribeToUpdates,
    eventChoices,
  });
  return data;
};
